/**
 * Cross-tab auth synchronisation.
 *
 * The authoritative staff session is the shared, httpOnly `auth_token` cookie — every tab and window
 * of the same browser already shares it. What the tabs do NOT share is the in-memory React auth state
 * (`user`/`userData` in AuthContext). Without a nudge, a login or logout in one tab leaves the others
 * showing their stale view until the next navigation or reload.
 *
 * This module is that nudge: a tiny pub/sub over `BroadcastChannel` (with a `localStorage` `storage`
 * event fallback for older browsers). It carries NO secrets — only the event kind — so it is safe even
 * though `BroadcastChannel` messages are readable by any same-origin script. On `logout` every tab
 * drops to the login screen; on `login` every tab re-bootstraps its session from the cookie.
 */

export type AuthBroadcastEvent = 'login' | 'logout';

const CHANNEL_NAME = 'clinvea-auth';
const STORAGE_FALLBACK_KEY = 'clinvea-auth-event';

type Listener = (event: AuthBroadcastEvent) => void;

const listeners = new Set<Listener>();

let channel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      const kind = e.data as AuthBroadcastEvent;
      if (kind === 'login' || kind === 'logout') {
        listeners.forEach((l) => l(kind));
      }
    };
  }
} catch {
  channel = null;
}

// storage-event fallback: writing to localStorage fires `storage` in OTHER tabs (never the writer).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_FALLBACK_KEY || !e.newValue) return;
    try {
      const kind = JSON.parse(e.newValue).kind as AuthBroadcastEvent;
      if (kind === 'login' || kind === 'logout') {
        listeners.forEach((l) => l(kind));
      }
    } catch {
      /* ignore malformed */
    }
  });
}

/** Announce an auth event to every OTHER tab of this browser. */
export function broadcastAuthEvent(event: AuthBroadcastEvent): void {
  try {
    channel?.postMessage(event);
  } catch {
    /* ignore */
  }
  if (!channel && typeof window !== 'undefined') {
    try {
      // A fresh value each time so repeated same-kind events still fire the storage listener.
      localStorage.setItem(STORAGE_FALLBACK_KEY, JSON.stringify({ kind: event, at: Date.now() }));
    } catch {
      /* ignore */
    }
  }
}

/** Subscribe to auth events from other tabs. Returns an unsubscribe function. */
export function onAuthEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
